import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface PaginationFooterControlsProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  itemsPerPage?: number;
  totalItems?: number;
  showPageSizeSelector?: boolean;
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
  className?: string;
}

const PaginationFooterControls = ({
  currentPage,
  totalPages,
  onPageChange,
  itemsPerPage,
  totalItems,
  showPageSizeSelector = false,
  pageSizeOptions = [10, 20, 50, 100],
  onPageSizeChange,
  className = "",
}: PaginationFooterControlsProps) => {
  const getPageNumbers = () => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [1];

    if (currentPage > 2) {
      pages.push("...");
    }

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let i = start; i <= end; i += 1) {
      pages.push(i);
    }

    if (currentPage < totalPages - 1) {
      pages.push("...");
    }
    pages.push(totalPages);

    return pages;
  };

  return (
    <div className={`flex items-center justify-between py-3 ${className}`}>
      <div>
        {(totalItems && itemsPerPage && (
          <p className="text-sm text-gray-700">
            Showing{" "}
            <span className="font-medium">
              {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}
            </span>{" "}
            to{" "}
            <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span>{" "}
            of <span className="font-medium">{totalItems}</span> results
          </p>
        )) ||
          null}

        <div className="mt-2 flex items-center space-x-4">
          <nav className="flex rounded-md shadow-sm" aria-label="Pagination">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`rounded-l-md px-2 py-2 ${
                currentPage === 1 ? "opacity-50" : "hover:bg-gray-50"
              }`}
            >
              &larr;
            </button>

            {getPageNumbers().map((page, index) =>
              typeof page === "number" ? (
                <button
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  onClick={() => onPageChange(page)}
                  className={`px-3 py-1 ${
                    currentPage === page ? "bg-blue-600 text-white" : "text-gray-700"
                  }`}
                >
                  {page}
                </button>
              ) : (
                <span
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className="px-2 py-1"
                >
                  {page}
                </span>
              ),
            )}

            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`rounded-r-md px-2 py-2 ${
                currentPage === totalPages ? "opacity-50" : "hover:bg-gray-50"
              }`}
            >
              &rarr;
            </button>
          </nav>

          {showPageSizeSelector && onPageSizeChange && (
            <div className="flex items-center">
              <span className="mr-2 text-sm">Per page:</span>
              <Select
                value={itemsPerPage?.toString()}
                defaultValue="10"
                onValueChange={value => onPageSizeChange(Number(value))}
              >
                <SelectTrigger className="w-16">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pageSizeOptions.map(option => (
                    <SelectItem key={option} value={option.toString()}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaginationFooterControls;
